import React, { useEffect, useRef, useImperativeHandle } from "react";
import { Form } from '../ui/universe';
import InputParams from '../params/InputParams';
import { Button, Radio, Tooltip, Tabs } from 'tdesign-react';
import { dealEduAgentRect, dealMarkInfoRect, dealEssayRect } from '../../utils/edu.js';
import { baseOcrRequest, addRecentMenu as addRecentMenuApi } from '../../api';
import { DownloadExcel, DownloadMd } from '../../utils/index.js';
import { showErrorMessage } from '../../utils/message.jsx';
import { v4 as uuidv4 } from 'uuid';
import { COS_URL, DEFAULT_EDU_AGENT_IMG_PREFIX } from '../../const/constants.js';
import ResponseParams from '../params/ResponseParams';
import { useRecursiveAxiosCaller } from '../../hooks';
import { useOcrStore, useUIStore, useImageStore, useMenuStore } from '../../store';
import { getCachedResponse, shouldCache, storeCachedResponse, CACHE_CONFIG } from '../../cache/index.js';
import '../../style/GeneralBasicOCR.css';

import {
  REGEX,
  DEFAULT_RESPONSE_TABS,
  MAX_RESULT_TIMES,
  MAX_RESULT_GAP,
} from '../../const/constants.js';

/**
 * GeneralBasicOCR组件：通用OCR识别组件，支持同步和异步OCR识别功能。
 */
export default function GeneralBasicOCR({
  cRef,
  onSelect,
  onChangeImg,
  onChangeImageEnhancement,
  onImageError,
  onResult,
}) {
  // 从 menuStore 获取当前菜单和操作
  const detail = useMenuStore(state => state.currentMenu);
  const addRecentMenu = useMenuStore(state => state.addRecentMenu);

  // 从 ocrStore 获取状态（使用选择器确保正确订阅）
  const paramsValue = useOcrStore(state => state.paramsValue);
  const requestParamsValue = useOcrStore(state => state.requestParamsValue);
  const paramsList = useOcrStore(state => state.paramsList);
  const smartKeyList = useOcrStore(state => state.smartKeyList);
  const apiType = useOcrStore(state => state.apiType);
  const request = useOcrStore(state => state.request);
  const response = useOcrStore(state => state.response);
  const rspTabs = useOcrStore(state => state.rspTabs);
  const currentTab = useOcrStore(state => state.currentTab);
  const rectOptions = useOcrStore(state => state.rectOptions);
  const showParams = useOcrStore(state => state.showParams);
  // 从 ocrStore 获取操作函数
  const {
    setParamsValue,
    setRequestParamsValue,
    setParamsList,
    setSmartKeyList,
    addSmartKey,
    updateSmartKey,
    removeSmartKey,
    setApiType,
    setRequest,
    setResponse,
    clearRequestResponse,
    setRspTabs,
    setCurrentTab,
    setRectOptions,
    setEduMarkData,
    setShowParams,
  } = useOcrStore();

  // 从 uiStore 获取状态（使用选择器确保正确订阅）
  const isLoading = useUIStore(state => state.isLoading);
  const isThinking = useUIStore(state => state.isThinking);
  const isMarkLoading = useUIStore(state => state.isMarkLoading);
  // 从 uiStore 获取操作函数
  const {
    setIsLoading,
    setIsThinking,
    setIsMarkLoading,
    stopLoading
  } = useUIStore();

  // 从 imageStore 获取状态（使用选择器确保正确订阅）
  const uploadImgList = useImageStore(state => state.uploadImgList);
  const image = useImageStore(state => state.currentImage);
  const clipImg = useImageStore(state => state.clipImg);
  const totalPage = useImageStore(state => state.totalPage);

  const messagesEnd = useRef(null);
  const paramsRef = useRef(null);

  const { start, stop, on } = useRecursiveAxiosCaller(MAX_RESULT_TIMES, MAX_RESULT_GAP, (res) => {
    return res?.JobStatus === 'DONE';
  });

  useImperativeHandle(cRef, () => ({
    start: ({ image: img, config }) => {
      const _smartKeyList = smartKeyList.filter(item => !item.isCustom);
      setSmartKeyList(_smartKeyList);
      const _params = {
        ...requestParamsValue,
        ...config
      };
      // 每次重新上传图片，将页码置为初始值
      if (_params.hasOwnProperty('FileEndPageNumber')) {
        _params.FileEndPageNumber = 1;
      }
      if (_params.hasOwnProperty('FileStartPageNumber')) {
        _params.FileStartPageNumber = 1;
      }
      setParamsValue(_params);
      setRequestParamsValue(_params);
      handleStart({ _params, _image: img, _smartKeyList });
    }
  }));

  // 注册事件监听器
  useEffect(() => {
    const cleanupFns = [];

    cleanupFns.push(on('onResponse', (res) => {
      setResponse(res);

      if (res?.Error || res?.error || res?.JobStatus === 'FAIL') {
        handleStop();
        setIsThinking(false);
        setIsLoading(false);
        showErrorMessage(res.message || res.msg);
      }
    }));

    cleanupFns.push(on('onComplete', (result, reason, _, extraData) => {
      handleStop();
      switch (reason) {
        case 'completed':
          successHandler(result, extraData);
          setIsThinking(false);
          // markEdu 的 isMarkLoading 已在 successHandler 中处理
          break;
        case 'stopped':
          stopLoading();
          break;
        case 'max_attempts':
        case 'error':
          stopLoading();
          showErrorMessage(result?.message || result?.msg);
          break;
        default:
          stopLoading();
      }
    }));

    return () => cleanupFns.forEach(fn => fn());
  }, [on]);

  const scrollToBottom = () => {
    if (messagesEnd?.current) {
      messagesEnd.current.scrollIntoView({ behavior: 'smooth', block: "end", inline: "nearest" });
    }
  };

  useEffect(() => {
    if (smartKeyList?.length > 0) {
      scrollToBottom();
    }
  }, [smartKeyList]);

  useEffect(() => {
    if (clipImg) {
      handleStartEduAgent({ clipImg });
    }
  }, [clipImg]);

  const handleResetParams = () => {
    handleStop();
    setParamsAndValue(detail.defaultApi, false, false);
    setSmartKeyList([]);
    if (detail.actionType === 'eduAgent' && rectOptions.length > 0) {
      onSelect(rectOptions[0].label, rectOptions[0].key, rectOptions[0]);
    }
  };

  const setParamsAndValue = (apitype, startImmediately = true, keepOldVal = false) => {
    const _value = {};
    let _paramsList = [];
    let _tab = detail.config?.Tabs || DEFAULT_RESPONSE_TABS;
    setApiType(apitype);

    if (apitype === 'sync' && detail.syncConfig) {
      _paramsList = detail.syncConfig.paramsList;
      _tab = detail.syncConfig.Tabs || DEFAULT_RESPONSE_TABS;
    } else if (apitype === 'async' && detail.asyncConfig) {
      _paramsList = detail.asyncConfig.paramsList;
    } else {
      _paramsList = detail.paramsList;
    }

    setParamsList(_paramsList);
    if (_paramsList?.length > 0) {
      _paramsList.forEach(e => {
        if (paramsValue.hasOwnProperty(e.key) && keepOldVal) {
          _value[e.key] = paramsValue[e.key];
        } else {
          _value[e.key] = JSON.parse(JSON.stringify(e.default));
        }
      });
    }

    setParamsValue({ ..._value });
    setRequestParamsValue({ ..._value });
    setRspTabs(_tab);
    setCurrentTab(_tab[0].id);

    if (startImmediately) {
      handleStart({ _params: _value, _image: image, _smartKeyList: [], _apiType: apitype });
    }
  };

  useEffect(() => {
    if (!detail) return;
    clearRequestResponse();
    // 切换菜单时不立即调用接口，由 OcrLayout 的 setConfig 统一触发
    // 避免使用旧图片调用新接口的问题
    setParamsAndValue(detail.defaultApi, false, false);
    setSmartKeyList([]);
  }, [detail]);

  const successHandler = async (res, extra) => {
    handleStop();
    setResponse(res);
    let rect = [];
    const pdfNum = extra.pdfNum;

    if (extra.config.actionType === 'markEdu') {
      // 更新 eduMarkData，合并原始提交结果和轮询结果
      const mergedData = {
        ...(extra.resSubmit || {}),
        ...res
      };
      setEduMarkData(mergedData);
      // 在设置数据后立即取消 loading，确保在同一个 React 更新批次中
      setIsMarkLoading(false);
      
      // markEdu 需要存储合并后的数据到缓存（包含 QuestionInfo 和 MarkInfos）
      // 因为 useRecursiveAxiosCaller 只会存储轮询结果，不包含提交时的 QuestionInfo
      // 注意：如果是缓存命中（extra.fromCache），则不需要再存储
      const cacheRequestConfig = extra.originalRequestConfig;
      if (!extra.fromCache && cacheRequestConfig && shouldCache(cacheRequestConfig, CACHE_CONFIG)) {
        try {
          // 使用当前时间作为缓存时间戳（最后获取到结果的时间）
          const cacheTimestamp = Date.now();
          await storeCachedResponse(cacheRequestConfig, mergedData, CACHE_CONFIG, cacheTimestamp);
          console.log(`[OCR Cache] markEdu 合并数据已缓存: ${cacheRequestConfig.cmd}`);
        } catch (cacheError) {
          console.error('[OCR Cache] markEdu 缓存存储失败:', cacheError);
        }
      }
      
      const rect1 = dealEduAgentRect(extra.resSubmit, false, pdfNum);
      const rect2 = dealMarkInfoRect(res, extra.resSubmit, pdfNum);
      rect = [...rect1, ...rect2];
      if (rect.length > 0 && rect[0]) {
        onSelect(rect[0].label, rect[0].key, rect[0]);
      }
    } else if (extra.config.resultConfig?.type === 'essayEdu') {
      rect = dealEssayRect(res, 0, 0, pdfNum);
    }

    onResult && onResult({
      uploadImglist: extra.uploadImglist,
      config: extra.config,
      ...extra.requestParams,
      rect,
      rsp: {
        ...res,
        auditData: extra.auditData
      }
    });
    setIsLoading(false);
  };

  const errorHandler = (res, extra) => {
    showErrorMessage(res.message || res.msg);
    setResponse(res);
    onResult && onResult({
      uploadImglist: extra.uploadImglist,
      isError: true,
      config: extra.config,
      ...extra.requestParams,
      rsp: {
        ...res,
        auditData: extra.auditData
      }
    });
    stopLoading();
  };

  const handleStop = async () => {
    stop();
  };

  const handleAddRecentMenu = async (InterfaceName) => {
    try {
      const res = await addRecentMenuApi({ InterfaceName });
      if (!res.error) {
        addRecentMenu(InterfaceName);
      }
    } catch (e) {
      // ignore error
    }
  };

  const handleStartEduAgent = async ({ clipImg: clipImgData }) => {
    if (!clipImgData) return;
    setIsThinking(true);
    clearRequestResponse();

    const payload = {
      ImageBase64: clipImgData.img
    };

    const res = await baseOcrRequest({
      cmd: detail.actionList[1],
      data: payload,
      isExample: clipImgData.img?.startsWith(DEFAULT_EDU_AGENT_IMG_PREFIX),
      fileType: 'png',
      module: detail.module || 'ocr'
    });

    handleAddRecentMenu(detail.actionList[1]);
    setIsThinking(false);
    setRequest({ ...payload });

    if (res.error) {
      showErrorMessage(res.message || res.msg);
    }
    setResponse(res);
  };

  const handleStart = async ({
    _params = requestParamsValue,
    _image = image,
    _smartKeyList = smartKeyList,
    _apiType = apiType
  } = {}) => {
    if (!_image) return;
    if (!validate(_smartKeyList)) return;

    handleStop();
    clearRequestResponse();

    let config = { ...detail };

    if (_apiType === 'sync' && detail.syncConfig) {
      config = { ...detail, ...detail.syncConfig };
    } else if (_apiType === 'async' && detail.asyncConfig) {
      config = { ...detail, ...detail.asyncConfig };
    } else {
      config = { ...detail, ...detail.asyncConfig };
    }

    if (config?.showCustomItem || config.resultConfig?.showCustomItem) {
      if (_smartKeyList?.length > 0) {
        _params.ItemNames = _smartKeyList.map(i => {
          return i.isCustom ? detail.options[i.value] : i.value;
        }).filter(n => n);
      } else {
        _params.ItemNames = [];
      }
    }

    if (config.actionType === 'eduAgent') {
      setIsLoading(true);
      setRectOptions([]);

      const payload = JSON.parse(JSON.stringify(config.actionList[0].params));
      const resSubmit = await baseOcrRequest({
        cmd: config.actionList[0].action,
        data: { ...payload, [_image.prefix]: _image.param ? _image.param : _image.url },
        isExample: _image.isExample,
        fileType: _image.fileType,
        module: config.module || 'ocr'
      });

      handleAddRecentMenu(config.actionList[1]);
      onImageError(resSubmit.isImageError || false);

      if (resSubmit.error) {
        setIsLoading(false);
        errorHandler(resSubmit, {
          uploadImglist: uploadImgList,
          config: { ...detail, action: detail.actionList[0].action },
          requestParams: { image: _image, requestParams: _params },
          auditData: resSubmit.auditData
        });
        setRequest({ ..._params, [_image.prefix]: _image.url });
      } else {
        const rect = dealEduAgentRect(resSubmit, true, _params.PdfPageNumber);
        setRectOptions(rect);
        setEduMarkData(resSubmit);
        setIsLoading(false);
        onResult && onResult({
          uploadImglist: uploadImgList,
          config: { ...detail, action: detail.actionList[0].action },
          image: _image,
          requestParams: _params,
          rsp: resSubmit,
          select: rect,
          rect: rect
        });
      }
    } else if (config.actionType === 'Async') {
      setIsLoading(true);

      const payload = JSON.parse(JSON.stringify(_params));
      if (!payload.PdfPageNumber) {
        delete payload.PdfPageNumber;
      }

      const dataParams = { ...payload };
      if (config.supportImgList) {
        dataParams[`${_image.prefix}List`] = _image.param ? [_image.param] : [_image.url];
      } else {
        dataParams[`${_image.prefix}`] = _image.param ? _image.param : _image.url;
      }

      // 构建原始提交请求配置，用于缓存 key
      const originalRequestConfig = {
        cmd: config.actionList[0],
        data: dataParams,
        isExample: _image.isExample,
        fileType: _image.fileType,
        module: config.module || 'ocr',
        paramsList: config.paramsList
      };

      // 先检查缓存，如果命中则直接使用缓存结果，跳过提交和轮询
      if (shouldCache(originalRequestConfig, CACHE_CONFIG)) {
        const cachedResponse = await getCachedResponse(originalRequestConfig, CACHE_CONFIG);
        if (cachedResponse) {
          console.log(`[OCR Cache] 异步接口缓存命中: ${config.actionList[0]}`);
          setRequest({ ..._params, [_image.prefix]: _image.url });
          successHandler(cachedResponse, {
            config,
            requestParams: { image: _image, requestParams: _params },
            uploadImglist: uploadImgList
          });
          return;
        }
      }

      const resSubmit = await baseOcrRequest({
        ...originalRequestConfig,
        skipCacheStore: true // 提交请求不缓存，等轮询成功后统一缓存
      });

      handleAddRecentMenu(config.actionList[0]);
      onImageError(resSubmit.isImageError || false);

      const JobId = config.supportImgList ? resSubmit.JobIds?.[0] : resSubmit.JobId;

      if (resSubmit.error || !JobId) {
        setIsLoading(false);
        errorHandler(resSubmit, {
          config,
          uploadImglist: uploadImgList,
          requestParams: { image: _image, requestParams: _params },
          auditData: resSubmit.auditData
        });
        setRequest({ ..._params, [_image.prefix]: _image.url });
      } else {
        const startPayload = {
          cmd: config.actionList[1],
          data: { JobId },
          isExample: _image.isExample,
          fileType: _image.fileType,
          module: config.module || 'ocr'
        };

        start(startPayload, {
          config,
          requestParams: { image: _image, requestParams: _params },
          uploadImglist: uploadImgList,
          auditData: resSubmit.auditData,
          originalRequestConfig // 传递原始请求配置用于缓存
        });

        setIsLoading(false);
        setIsThinking(true);
        setRequest({ ..._params, [_image.prefix]: _image.url });
      }
    } else if (config.actionType === 'markEdu') {
      const payload = JSON.parse(JSON.stringify(_params));
      if (!payload.PdfPageNumber) {
        delete payload.PdfPageNumber;
      }

      // 构建原始提交请求配置，用于缓存 key
      const originalRequestConfig = {
        cmd: config.actionList[0],
        data: { ...payload, [_image.prefix]: _image.param ? _image.param : _image.url },
        isExample: _image.isExample,
        fileType: _image.fileType,
        module: config.module || 'ocr',
        paramsList: config.paramsList
      };

      // 先检查缓存，如果命中则直接使用缓存结果，跳过提交和轮询
      if (shouldCache(originalRequestConfig, CACHE_CONFIG)) {
        const cachedResponse = await getCachedResponse(originalRequestConfig, CACHE_CONFIG);
        if (cachedResponse) {
          console.log(`[OCR Cache] markEdu 缓存命中: ${config.actionList[0]}`);
          setRequest({ ..._params, [_image.prefix]: _image.url });
          successHandler(cachedResponse, {
            config,
            requestParams: { image: _image, requestParams: _params },
            uploadImglist: uploadImgList,
            resSubmit: cachedResponse, // 使用缓存数据作为 resSubmit
            pdfNum: _params.PdfPageNumber,
            originalRequestConfig,
            fromCache: true // 标记为缓存命中，避免重复存储
          });
          return;
        }
      }

      const resSubmit = await baseOcrRequest({
        ...originalRequestConfig,
        skipCacheStore: true // 提交请求不缓存，等轮询成功后统一缓存
      });

      handleAddRecentMenu(config.actionList[0]);
      onImageError(resSubmit.isImageError || false);

      if (resSubmit.error || !resSubmit.JobId) {
        setIsLoading(false);
        setEduMarkData(resSubmit);
        errorHandler(resSubmit, {
          config,
          uploadImglist: uploadImgList,
          requestParams: { image: _image, requestParams: _params },
          auditData: resSubmit.auditData
        });
        setRequest({ ..._params, [_image.prefix]: _image.url });
      } else {
        const startPayload = {
          cmd: config.actionList[1],
          data: { JobId: resSubmit.JobId },
          isExample: _image.isExample,
          fileType: _image.fileType,
          module: config.module || 'ocr'
        };

        const rect = dealEduAgentRect(resSubmit, false, _params.PdfPageNumber);

        onResult && onResult({
          uploadImglist: uploadImgList,
          config: { ...detail, action: detail.actionList[0].action },
          image: _image,
          requestParams: _params,
          rsp: resSubmit,
          select: rect,
          rect: rect
        });

        // 设置 eduMarkData 时，移除可能存在的旧 MarkInfos，确保 loading 能正常显示
        // 只保留 QuestionInfo 等基础数据，MarkInfos 在轮询成功后才设置
        const submitDataWithoutMark = { ...resSubmit };
        delete submitDataWithoutMark.MarkInfos;
        if (submitDataWithoutMark.data?.Response) {
          submitDataWithoutMark.data = {
            ...submitDataWithoutMark.data,
            Response: { ...submitDataWithoutMark.data.Response }
          };
          delete submitDataWithoutMark.data.Response.MarkInfos;
        }
        setEduMarkData(submitDataWithoutMark);

        // 先设置 isMarkLoading，再取消 isLoading，避免渲染时 loading 状态不一致
        setIsMarkLoading(true);
        setIsLoading(false);
        setRequest({ ..._params, [_image.prefix]: _image.url });

        start(startPayload, {
          config,
          requestParams: { image: _image, requestParams: _params },
          resSubmit,
          uploadImglist: uploadImgList,
          auditData: resSubmit.auditData,
          pdfNum: _params.PdfPageNumber,
          originalRequestConfig // 传递原始请求配置用于缓存
        });
      }
    } else {
      setIsLoading(true);

      const res = await baseOcrRequest({
        cmd: config.action,
        data: { ..._params, [_image.prefix]: _image.param ? _image.param : _image.url },
        isExample: _image.isExample,
        fileType: _image.fileType,
        module: config.module || 'ocr',
        paramsList: config.paramsList
      });

      handleAddRecentMenu(config.action);
      onImageError(res.isImageError || false);
      setRequest({ ..._params, [_image.prefix]: _image.url });

      if (res.error) {
        errorHandler(res, {
          config,
          uploadImglist: uploadImgList,
          requestParams: { image: _image, requestParams: _params },
          auditData: res.auditData
        });
      } else {
        if (config.isImageResult) {
          const imageField = config.resultConfig?.imageResultField || 'Image';
          onChangeImageEnhancement && onChangeImageEnhancement({
            type: _params.TaskType === 1 ? 'col' : 'split',
            url: res[imageField]
          });
        }
        successHandler(res, {
          config,
          requestParams: { image: _image, requestParams: _params },
          auditData: res.auditData,
          uploadImglist: uploadImgList
        });
      }
    }
  };

  const handleAddSmart = (type) => {
    const _key = uuidv4();
    addSmartKey({
      isCustom: type !== 'custom',
      showClose: true,
      type: type === 'custom' ? 'input' : 'select',
      key: _key,
      default: '',
      value: '',
      status: ''
    });
  };

  const handleDelSmart = (_, index) => {
    removeSmartKey(index);
  };

  const validate = (_smartKeyList = smartKeyList) => {
    const result = _smartKeyList.map(item => {
      if (REGEX.WHITESPACE.test(item.value)) {
        return { ...item, status: 'error' };
      } else {
        return { ...item, status: '' };
      }
    });
    setSmartKeyList([...result]);
    return result.filter(i => i.status === 'error').length === 0;
  };

  return (
    <div className="result-panel">
      {
        paramsList?.filter(i => !i.isHide)?.length > 0 &&
        <div className={`result-panel-params__container ${showParams ? 'open' : 'close'}`} ref={paramsRef}>
          <div className="result-panel-params__content">
            <header className="ocrDemo-result__panel-hd cantOpen">
              <div className="ocrDemo-result-title">
                <span className="ocrDemo-result-title__icon"></span>
                <span className="ocrDemo-result-title__text">{detail?.config?.ArgumentText || '参数调整'}</span>
                <span className="ocrDemo-result-title__reset" onClick={handleResetParams}>
                  <span className="ocrDemo-icon ocrDemo-icon_reset"></span>恢复默认
                </span>
              </div>
              <div className="unv-collapse__panel-collops" onClick={() => setShowParams(!showParams)}></div>
            </header>
            <div className="ocrDemo-result-from-container unv-form unv-form--split" style={{ padding: "0 20px" }}>
              {
                detail.syncConfig &&
                <Form labelAlign="left">
                  <Form.Item className="row" label={detail.syncConfig.asyncLabel}>
                    <Radio.Group value={apiType} onChange={(e) => setParamsAndValue(e, true, true)}>
                      <Radio key="sync" value="sync">
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <span>实时模型</span>
                          {detail.syncConfig.asyncDes &&
                            <Tooltip content={detail.syncConfig.asyncDes} theme="light">
                              <span className="ocrDemo-icon-question"></span>
                            </Tooltip>
                          }
                        </div>
                      </Radio>
                      <Radio key="async" value="async">
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <span>异步模型</span>
                          {detail.syncConfig.asyncDes &&
                            <Tooltip content={detail.asyncConfig.asyncDes} theme="light">
                              <span className="ocrDemo-icon-question"></span>
                            </Tooltip>
                          }
                        </div>
                      </Radio>
                    </Radio.Group>
                  </Form.Item>
                </Form>
              }

              <Form labelAlign={detail?.config?.LabelAlign || 'left'}>
                {
                  paramsList?.filter(i => !i.isHide)?.map((params) => {
                    return params.key in paramsValue && <InputParams
                      clipImg={clipImg}
                      rectOptions={rectOptions}
                      key={params.key}
                      params={{
                        ...params,
                        min: typeof params.min === "string" ? paramsValue[params.min] : params.min || 1,
                        max: totalPage,
                      }}
                      value={paramsValue[params.key]}
                      showClose={params.showClose}
                      onSelect={onSelect}
                      onChange={(val, realVal, extra) => {
                        const _paramsValue = { ...paramsValue, [params.key]: val };
                        const _requestVal = { ...paramsValue, [params.key]: realVal || val };
                        setParamsValue(_paramsValue);
                        setRequestParamsValue(_requestVal);
                        if (extra?.startImmediately && image.isExample) {
                          const defaultImg = COS_URL + extra.defaultImg;
                          // 如果选项包含 defaultConfig，则合并到请求参数中
                          const mergedParams = extra.defaultConfig 
                            ? { ..._requestVal, ...extra.defaultConfig }
                            : _requestVal;
                          onChangeImg && onChangeImg(defaultImg, mergedParams);
                        }
                      }}
                    />;
                  })
                }
                {
                  smartKeyList.filter(i => !i.isHide)?.map((params, index) => {
                    return <InputParams
                      key={params.key}
                      params={{
                        ...params,
                        label: params.label || `字段${index + 1}`,
                        options: params.isCustom ? detail?.options?.map((item, idx) => ({
                          text: item,
                          value: idx
                        })) : params.options
                      }}
                      status={params.status}
                      placeholder={params.placeholder}
                      value={params.value}
                      showClose={params.showClose}
                      onClose={() => handleDelSmart(params, index)}
                      onChange={(val) => {
                        updateSmartKey(params.key, {
                          value: val,
                          status: REGEX.WHITESPACE.test(val) ? 'error' : ''
                        });
                      }}
                    />;
                  })
                }
                {
                  (detail?.showCustomItem || detail?.resultConfig?.showCustomItem) &&
                  <p style={{ marginTop: '8px', marginBottom: '20px' }}>
                    <Button theme="default" variant="outline" disabled={isLoading} onClick={() => handleAddSmart('default')}>
                      <span className="ocrDemo-icon_add"></span>
                      <span>默认key</span>
                    </Button>
                    <Button theme="default" variant="outline" disabled={isLoading} onClick={() => handleAddSmart('custom')} style={{ marginLeft: '20px' }}>
                      <span className="ocrDemo-icon_add"></span>
                      <span>自定义key</span>
                    </Button>
                  </p>
                }
                <div className="message_scroll__bar" style={{ clear: 'both', height: '1px', width: '100%' }} ref={messagesEnd}></div>
              </Form>
            </div>
          </div>
        </div>
      }
      <div className="ocrDemo-result-response-container">
        <Tabs
          className="ocrDemo-result-response__tab"
          defaultValue={currentTab}
          value={currentTab}
          onChange={(value) => setCurrentTab(value)}
        >
          {
            rspTabs.map(item => (
              <Tabs.TabPanel value={item.id} label={item.label} key={item.id}>
                <ResponseParams
                  type={item.type}
                  onSelect={onSelect}
                />
              </Tabs.TabPanel>
            ))
          }
        </Tabs>
        <footer className="unv-collapse__panel__footer">
          <div className="ocrDemo-footer-btn-container">
            {
              detail?.showDownBtn && <>
                {
                  request[detail.resultConfig?.markdownKey] === 'Mdbase64' &&
                  <Button theme="default" variant="outline" onClick={() => DownloadMd(response[detail.resultConfig?.markdownContent], 'md', '结果')}>导出Markdown结果</Button>
                }
                {
                  request[detail.resultConfig?.markdownKey] === 'Excelbase64' &&
                  <Button theme="default" variant="outline" onClick={() => DownloadExcel(response[detail.resultConfig?.markdownContent], '结果')}>导出Excel结果</Button>
                }
              </>
            }
            {
              (detail?.actionType === 'Async' || apiType === 'async' || detail?.actionType === 'markEdu') ? (
                <div className="ocrDemo-result__btn" onClick={() => {
                  if (isThinking || isMarkLoading) {
                    handleStop();
                  } else {
                    if (isLoading) return;
                    handleStart({ _params: requestParamsValue });
                  }
                }}>
                  <div className={`ocrDemo-result__btn_content ${isThinking || isMarkLoading ? 'stop' : 'start'}`}></div>
                  <div className="ocrDemo-result__btn_border"></div>
                </div>
              ) : detail?.actionType === 'eduAgent' ? (
                <div className="ocrDemo-result__btn" onClick={() => handleStartEduAgent({ clipImg })}>
                  <div className="ocrDemo-result__btn_content start"></div>
                  <div className="ocrDemo-result__btn_border"></div>
                </div>
              ) : (
                <div className="ocrDemo-result__btn" onClick={() => handleStart({ _params: requestParamsValue })}>
                  <div className="ocrDemo-result__btn_content"></div>
                  <div className="ocrDemo-result__btn_border"></div>
                </div>
              )
            }
          </div>
        </footer>
      </div>
    </div>
  );
}
